import React, { useEffect, useMemo, useRef } from 'react';
import { Box, Button, H2, H3, Panel, Text, Form, FormGroup, Input, Select, Textarea, Switch, Flex, FlexItem, Grid, GridItem } from '@bigcommerce/big-design';
import { StarIcon } from '@bigcommerce/big-design-icons';
import { useHistory, useLocation } from 'react-router-dom';
import InteractiveStarRating from './InteractiveStarRating';
import { useSelector, useDispatch } from 'react-redux';
import { updateReview, addReview } from '../reviewsSlice';
import { searchProducts, searchProduct } from '../productsSlice';
import { useFormik } from 'formik';
import { throttle } from 'lodash';
import { alertsManager } from '../App';

/**
 * Component chỉnh sửa hoặc thêm mới review
 * @param {Object} review - Dữ liệu review cần chỉnh sửa (null nếu tạo mới)
 * @param {string} backUrl - URL để quay lại
 */
const ReviewEdit = ({ review, backUrl }) => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const loading = useSelector(state => state.reviews.loading);
    const search = useSelector(state => state.products.search);
    const searchByProductId = review ? useSelector(state => state.products.searchByProductId[review.productId]) : null;
    const productSelectOptions = [...search] || [];
    const { data: settings } = useSelector(state => state.settings);
    const isMountedRef = useRef(true);

    if (searchByProductId && searchByProductId.length > 0) {
        const ids = search.map(item => item.value);
        searchByProductId.forEach(item => {
            if (!ids.includes(item.value)) {
                productSelectOptions.push(item);
            }
        });
    }

    // Cleanup mounted ref khi component unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (review && review.productId && isMountedRef.current) {
            dispatch(searchProduct(review.productId));
        }
    }, []);

    // Khởi tạo giá trị cho tiêu chí đánh giá mở rộng với useMemo để tránh re-compute
    const initialCriteriaRatings = useMemo(() => {
        if (!settings?.enableReviewCriteria || !settings?.reviewCriteria) return {};

        const criteriaRatings = {};
        settings.reviewCriteria.forEach(criterion => {
            if (criterion.enabled) {
                const key = `criteria_${criterion.id}`;
                if (review && review.criteriaRatings && review.criteriaRatings[criterion.id] !== undefined) {
                    criteriaRatings[key] = review.criteriaRatings[criterion.id];
                } else {
                    // Giá trị mặc định: 0 cho optional criteria, giữ nguyên 5 cho required
                    criteriaRatings[key] = criterion.type === 'rating' ? (criterion.required ? 5 : 0) : false;
                }
            }
        });
        return criteriaRatings;
    }, [settings?.enableReviewCriteria, settings?.reviewCriteria, review?.criteriaRatings, review?.reviewId]);

    const form = useFormik({
        initialValues: {
            reviewId: review ? review.reviewId : '',
            email: review ? review.email : '',
            name: review ? review.name : '',
            productId: review ? review.productId : '',
            rating: review ? review.rating : 5,
            status: review ? review.status : 'approved',
            text: review ? review.text : '',
            title: review ? review.title : '',
            isFeatured: review?.isFeatured ?? false,
            ...initialCriteriaRatings,
        },
        onSubmit: values => {
            // Xử lý criteria ratings
            const criteriaRatings = {};
            if (settings?.enableReviewCriteria && settings?.reviewCriteria) {
                settings.reviewCriteria.forEach(criterion => {
                    if (criterion.enabled) {
                        const key = `criteria_${criterion.id}`;
                        const value = values[key];
                        // Chỉ gửi criteria có giá trị hợp lệ (rating > 0, hoặc boolean bất kỳ)
                        if (value !== undefined) {
                            if (criterion.type === 'rating') {
                                // Không gửi rating = 0 (chưa đánh giá)
                                if (value > 0) {
                                    criteriaRatings[criterion.id] = value;
                                }
                            } else {
                                criteriaRatings[criterion.id] = value;
                            }
                        }
                    }
                });
            }

            // Tạo payload với criteria ratings
            const reviewData = {
                ...values,
                criteriaRatings: Object.keys(criteriaRatings).length > 0 ? criteriaRatings : undefined,
            };

            // Loại bỏ các criteria fields khỏi payload chính
            Object.keys(values).forEach(key => {
                if (key.startsWith('criteria_')) {
                    delete reviewData[key];
                }
            });

            if (values.reviewId) {
                dispatch(updateReview(reviewData));
            } else {
                dispatch(addReview(reviewData));
            }

            // Chỉ setSubmitting nếu component vẫn mounted
            if (isMountedRef.current) {
                form.setSubmitting(false);
            }
        },
    });

    useEffect(() => {
        if (form.isSubmitting && loading == 'idle') {
            // Chỉ update state nếu component vẫn mounted
            if (isMountedRef.current) {
                form.setSubmitting(false);
                history.push(backUrl);
                alertsManager.add({
                    header: 'Review saved',
                    messages: [{
                        text: review ? review.title : '',
                    }],
                    type: 'success',
                    autoDismiss: true,
                })
            }
        }
    }, [form.isSubmitting, loading]);

    const findProducts = throttle(keyword => {
        if (keyword.length >= 3 && isMountedRef.current) {
            dispatch(searchProducts(keyword));
        }
    }, 500, { leading: false });

    // Cancel throttled function khi component unmount
    useEffect(() => {
        return () => {
            findProducts.cancel();
        };
    }, []);

    return (
        <Panel>
            <Flex alignItems="center" justifyContent="space-between" marginBottom="large">
                <FlexItem>
                    <H2 margin="none">{review ? `Edit Review: ${review.title}` : 'Add Review'}</H2>
                </FlexItem>
                <FlexItem>
                    <Button variant="secondary" onClick={() => history.push(backUrl)}>Back</Button>
                </FlexItem>
            </Flex>
            <Form onSubmit={form.handleSubmit}>
                <FormGroup>
                    <Input
                        name="name"
                        label="Name"
                        type="text"
                        value={form.values.name}
                        onChange={form.handleChange}
                        error={form.touched.name && form.errors.name}
                        required
                    />
                </FormGroup>
                <FormGroup>
                    <Input
                        name="email"
                        label="Email"
                        type="email"
                        value={form.values.email}
                        onChange={form.handleChange}
                        error={form.touched.email && form.errors.email}
                        required
                    />
                </FormGroup>
                <FormGroup>
                    <Select
                        name="productId"
                        label="Product"
                        onOptionChange={value => {
                            if (isMountedRef.current) {
                                form.setFieldValue('productId', value);
                            }
                        }}
                        options={productSelectOptions}
                        placeholder={'Choose product'}
                        value={form.values.productId}
                        error={form.touched.productId && form.errors.productId}
                        onChange={e => findProducts(e.target.value)}
                        required
                    />
                </FormGroup>
                <FormGroup>
                    <Box>
                        <Text bold marginBottom="xSmall">Rating</Text>
                        <InteractiveStarRating
                            value={form.values.rating}
                            onChange={(value) => {
                                if (isMountedRef.current) {
                                    form.setFieldValue('rating', value);
                                }
                            }}
                        />
                        {form.touched.rating && form.errors.rating && (
                            <Text color="danger" marginTop="xSmall">{form.errors.rating}</Text>
                        )}
                    </Box>
                </FormGroup>
                <FormGroup>
                    <Select
                        name="status"
                        label="Status"
                        onOptionChange={value => {
                            if (isMountedRef.current) {
                                form.setFieldValue('status', value);
                            }
                        }}
                        options={[
                            { value: 'approved', content: 'Approved' },
                            { value: 'pending', content: 'Pending' },
                            { value: 'disapproved', content: 'Disapproved' },
                        ]}
                        filterable={false}
                        value={form.values.status}
                        error={form.touched.status && form.errors.status}
                        required
                    />
                </FormGroup>
                <FormGroup>
                    <Input
                        name="title"
                        label="Title"
                        type="text"
                        value={form.values.title}
                        onChange={form.handleChange}
                        error={form.touched.title && form.errors.title}
                        required
                    />
                </FormGroup>
                <FormGroup>
                    <Textarea
                        name="text"
                        label="Text"
                        value={form.values.text}
                        onChange={form.handleChange}
                        error={form.touched.text && form.errors.text}
                        required
                    />
                </FormGroup>
                <FormGroup>
                    <Box>
                        <Text bold margin='none'>Is featured ?</Text>
                        <Box style={{fontStyle: 'italic'}} marginBottom='small'>(Only approved reviews can be featured. Otherwise, 'Is Featured' will be turned off when saving.)</Box>
                        <Box style={{fontStyle: 'italic'}} marginBottom='small'>(You can't set a review as featured while creating it.)</Box>
                        <Switch
                            name='isFeatured'
                            checked={!!form.values.isFeatured}
                            onChange={(e) => {
                                if (isMountedRef.current) {
                                    const checked = e.target.checked;
                                    form.setFieldValue('isFeatured', checked);
                                }
                            }}
                            disabled={!review}
                        />
                    </Box>
                </FormGroup>

                {/* Tiêu chí đánh giá mở rộng */}
                {settings?.enableReviewCriteria && settings?.reviewCriteria && (
                    <Box marginTop="medium">
                        <Text bold marginBottom="small"><StarIcon color="warning" /> Review Criteria</Text>
                        <Grid gridColumns={{ mobile: '1fr', tablet: '1fr 1fr' }} gridGap="1rem">
                            {settings.reviewCriteria
                                .filter(criterion => criterion.enabled)
                                .map((criterion) => {
                                    const fieldName = `criteria_${criterion.id}`;
                                    return (
                                        <GridItem key={criterion.id}>
                                            <Flex alignItems="center" justifyContent="space-between"
                                                style={{
                                                    padding: '8px 12px',
                                                    backgroundColor: '#f9f9f9',
                                                    borderRadius: '4px',
                                                    minHeight: '40px'
                                                }}>
                                                <Text margin="none" style={{ flex: 1 }}>
                                                    {criterion.label}
                                                    {!criterion.required && <Text as="span" color="secondary60"> (optional)</Text>}
                                                </Text>
                                                {criterion.type === 'rating' ? (
                                                    <InteractiveStarRating
                                                        value={form.values[fieldName]}
                                                        onChange={(value) => {
                                                            if (isMountedRef.current) {
                                                                form.setFieldValue(fieldName, value);
                                                            }
                                                        }}
                                                        size="small"
                                                        showLabel={false}
                                                        allowClear={!criterion.required}
                                                    />
                                                ) : (
                                                    <Switch
                                                        name={fieldName}
                                                        checked={!!form.values[fieldName]}
                                                        onChange={(e) => {
                                                            if (isMountedRef.current) {
                                                                form.setFieldValue(fieldName, e.target.checked);
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </Flex>
                                        </GridItem>
                                    );
                                })}
                        </Grid>
                    </Box>
                )}
                <Box marginTop="xxLarge">
                    <Button isLoading={loading !== 'idle'}>Save</Button>
                </Box>
            </Form>
        </Panel>
    );
}

export default ReviewEdit;
